import { usePathHelper } from '@/hooks/usePathHelper'
import {
  clearScheduleFixedEndDate,
  handleFixedEndDateScheduleChange,
  handleStartHolidayScheduleChange,
  switchScheduleToFixedDate,
  switchScheduleToHoliday,
} from '@/components/forms/ScheduleAutomationFields'
import dayjs from '@/lib/dayjs'
import { useAppForm } from '@/lib/form'
import { useNavigate } from '@tanstack/react-router'
import { useId, useRef, useState } from 'react'
import { toast } from 'sonner'
import { useCollections } from '@/lib/services/collections/collections.query'
import { isSubscriptionPackageCollection } from '@/lib/subscription-package-collection'
import {
  useCreatePriceAdjustmentMutation,
  useDisablePriceAdjustmentMutation,
  useDeletePriceAdjustmentMutation,
  useEnablePriceAdjustmentMutation,
  useUpdatePriceAdjustmentMutation,
} from '@/lib/services/price-adjustments/priceAdjustments.query'
import {
  getEmptyPriceAdjustmentValues,
  mapPriceAdjustmentToFormValues,
  normalizePriceAdjustmentPayload,
  PRICE_ADJUSTMENT_DISABLE_MODE_THIS_OCCURRENCE,
  PRICE_ADJUSTMENT_END_TYPE_NONE,
  PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE,
  PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET,
  PRICE_ADJUSTMENT_START_TYPE_PERMANENT,
  PRICE_ADJUSTMENT_WHEN_TYPE_ACTIVE_UNTIL_DELETED,
  PRICE_ADJUSTMENT_WHEN_TYPE_PERMANENT,
  PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED,
  priceAdjustmentForm,
} from '@/lib/services/price-adjustments/priceAdjustments.schema'
import { priceAdjustmentIsSuppressed } from '@/lib/services/price-adjustments/priceAdjustments.summary'
import { getPriceAdjustmentErrorMessage, getPriceAdjustmentScheduleBounds } from './helpers'

export function useProductPriceAdjustmentFormController({
  initialData = null,
  prefillCollectionId = '',
  collectionOptions = [],
  showPageTreatment = false,
}) {
  const id = useId()
  const navigate = useNavigate()
  const path = usePathHelper()
  const { data: loadedCollections = [] } = useCollections({ filters: {} })

  const createMutation = useCreatePriceAdjustmentMutation()
  const disableMutation = useDisablePriceAdjustmentMutation()
  const updateMutation = useUpdatePriceAdjustmentMutation()
  const deleteMutation = useDeletePriceAdjustmentMutation({ cleanupOnSuccess: false })
  const enableMutation = useEnablePriceAdjustmentMutation()
  const [latestPersistedAdjustment, setLatestPersistedAdjustment] = useState(null)
  const [disableDialogOpen, setDisableDialogOpen] = useState(false)
  const [permanentConfirmationOpen, setPermanentConfirmationOpen] = useState(false)

  const mutation = initialData ? updateMutation : createMutation

  const collections = (collectionOptions?.length ? collectionOptions : loadedCollections) ?? []
  const selectableCollections = collections
    .filter((collection) => !isSubscriptionPackageCollection(collection))
    .map((collection) => ({ id: collection.id, name: collection.name }))
    .sort((left, right) => left.name.localeCompare(right.name))
  const { minDate: scheduleMinDate, maxDate: scheduleMaxDate } =
    getPriceAdjustmentScheduleBounds(initialData)

  const defaultValues = initialData
    ? mapPriceAdjustmentToFormValues(initialData)
    : getEmptyPriceAdjustmentValues({ collectionId: prefillCollectionId })

  const form = useAppForm({
    defaultValues,
    validators: {
      onSubmit: priceAdjustmentForm,
      onSubmitAsync: async ({ value, formApi }) => {
        try {
          const payload = normalizePriceAdjustmentPayload(value)
          const response = await mutation.mutateAsync(
            initialData ? { ...payload, priceAdjustmentId: initialData.id } : payload
          )

          if (response?.permanent) {
            const indexPath = path.priceChanges.index()

            toast.success('Prices updated permanently')
            void navigate({
              ...(indexPath.link ?? indexPath),
              ignoreBlocker: true,
            })

            return
          }

          if (initialData) {
            toast.success('Price change updated successfully')
            setLatestPersistedAdjustment(response)
            setTimeout(() => formApi.reset(mapPriceAdjustmentToFormValues(response)))
          } else {
            toast.success('Price change created successfully')
            void navigate({
              ...path.priceChanges.show(response?.id).link,
              ignoreBlocker: true,
            })
          }
        } catch (error) {
          toast.error(`Price change ${initialData ? 'update' : 'creation'} failed`, {
            description: getPriceAdjustmentErrorMessage(error),
          })

          if (error?.response?.data?.errors) {
            return {
              fields: error.response.data.errors,
            }
          }
        }
      },
    },
  })

  const getScheduledTimingDraft = (schedule = {}) => ({
    start_type: schedule?.start_type || PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET,
    starts_on: schedule?.starts_on || '',
    start_holiday: schedule?.start_holiday || '',
    start_offset_days: schedule?.start_offset_days ?? '',
    end_type: schedule?.end_type || PRICE_ADJUSTMENT_END_TYPE_NONE,
    ends_on: schedule?.ends_on || '',
    end_holiday: schedule?.end_holiday || '',
    end_offset_days: schedule?.end_offset_days ?? '',
    end_days_after_start: schedule?.end_days_after_start ?? '',
    is_yearly: Boolean(schedule?.is_yearly),
    end_matches_start_holiday: schedule?.end_matches_start_holiday !== false,
  })

  const scheduledTimingDraftRef = useRef(
    defaultValues.when_type === PRICE_ADJUSTMENT_WHEN_TYPE_SCHEDULED
      ? getScheduledTimingDraft(defaultValues.schedule)
      : null
  )

  const rememberCurrentScheduledTimingDraft = () => {
    scheduledTimingDraftRef.current = getScheduledTimingDraft({
      start_type: form.getFieldValue('schedule.start_type'),
      starts_on: form.getFieldValue('schedule.starts_on'),
      start_holiday: form.getFieldValue('schedule.start_holiday'),
      start_offset_days: form.getFieldValue('schedule.start_offset_days'),
      end_type: form.getFieldValue('schedule.end_type'),
      ends_on: form.getFieldValue('schedule.ends_on'),
      end_holiday: form.getFieldValue('schedule.end_holiday'),
      end_offset_days: form.getFieldValue('schedule.end_offset_days'),
      end_days_after_start: form.getFieldValue('schedule.end_days_after_start'),
      is_yearly: form.getFieldValue('schedule.is_yearly'),
      end_matches_start_holiday: form.getFieldValue('schedule.end_matches_start_holiday'),
    })
  }

  const clearFieldErrors = (fieldName) => {
    form.setFieldMeta(fieldName, (meta) => {
      const currentErrors = Array.isArray(meta?.errors) ? meta.errors : []
      const currentErrorMap = meta?.errorMap ?? {}

      if (currentErrors.length === 0 && Object.keys(currentErrorMap).length === 0) {
        return meta
      }

      return {
        ...meta,
        errors: [],
        errorMap: {},
      }
    })
  }

  const clearScheduleSectionError = () => {
    clearFieldErrors('schedule')
  }

  const clearScopeSectionError = () => {
    clearFieldErrors('collection_ids')
  }

  const clearWhenSectionError = () => {
    clearFieldErrors('when_type')
  }

  const handleScheduleFieldChange = (listener) => (payload) => {
    clearScheduleSectionError()
    listener?.(payload)
  }

  const todayDate = dayjs().format('YYYY-MM-DD')

  const switchToFixedDateSchedule = () => {
    switchScheduleToFixedDate({ form, todayDate })
  }

  const switchToHolidaySchedule = () => {
    switchScheduleToHoliday({ form })
  }

  const switchToActiveUntilDeletedMode = () => {
    const currentStartType = form.getFieldValue('schedule.start_type')
    const currentStartsOn = form.getFieldValue('schedule.starts_on')

    form.setFieldValue('schedule.start_type', PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE)
    form.setFieldValue(
      'schedule.starts_on',
      currentStartType === PRICE_ADJUSTMENT_START_TYPE_FIXED_DATE && currentStartsOn
        ? currentStartsOn
        : todayDate
    )
    form.setFieldValue('schedule.start_holiday', '')
    form.setFieldValue('schedule.start_occasion_id', null)
    form.setFieldValue('schedule.start_offset_days', '')
    form.setFieldValue('schedule.end_type', PRICE_ADJUSTMENT_END_TYPE_NONE)
    form.setFieldValue('schedule.ends_on', '')
    form.setFieldValue('schedule.end_holiday', '')
    form.setFieldValue('schedule.end_occasion_id', null)
    form.setFieldValue('schedule.end_offset_days', '')
    form.setFieldValue('schedule.end_days_after_start', '')
    form.setFieldValue('schedule.is_yearly', false)
    form.setFieldValue('schedule.end_matches_start_holiday', true)
  }

  const switchToScheduledMode = () => {
    const scheduledTimingDraft = scheduledTimingDraftRef.current

    if (scheduledTimingDraft) {
      form.setFieldValue('schedule.start_type', scheduledTimingDraft.start_type)
      form.setFieldValue('schedule.starts_on', scheduledTimingDraft.starts_on)
      form.setFieldValue('schedule.start_holiday', scheduledTimingDraft.start_holiday)
      form.setFieldValue('schedule.start_offset_days', scheduledTimingDraft.start_offset_days)
      form.setFieldValue('schedule.end_type', scheduledTimingDraft.end_type)
      form.setFieldValue('schedule.ends_on', scheduledTimingDraft.ends_on)
      form.setFieldValue('schedule.end_holiday', scheduledTimingDraft.end_holiday)
      form.setFieldValue('schedule.end_offset_days', scheduledTimingDraft.end_offset_days)
      form.setFieldValue('schedule.end_days_after_start', scheduledTimingDraft.end_days_after_start)
      form.setFieldValue('schedule.is_yearly', scheduledTimingDraft.is_yearly)
      form.setFieldValue(
        'schedule.end_matches_start_holiday',
        scheduledTimingDraft.end_matches_start_holiday
      )
      return
    }

    form.setFieldValue('schedule.start_type', PRICE_ADJUSTMENT_START_TYPE_HOLIDAY_OFFSET)
    switchToHolidaySchedule()
  }

  const switchToPermanentSchedule = () => {
    form.setFieldValue('schedule.start_type', PRICE_ADJUSTMENT_START_TYPE_PERMANENT)
    form.setFieldValue('schedule.is_yearly', false)
    form.setFieldValue('schedule.starts_on', '')
    form.setFieldValue('schedule.start_holiday', '')
    form.setFieldValue('schedule.start_occasion_id', null)
    form.setFieldValue('schedule.start_offset_days', '')
    form.setFieldValue('schedule.end_type', PRICE_ADJUSTMENT_END_TYPE_NONE)
    form.setFieldValue('schedule.ends_on', '')
    form.setFieldValue('schedule.end_holiday', '')
    form.setFieldValue('schedule.end_occasion_id', null)
    form.setFieldValue('schedule.end_offset_days', '')
    form.setFieldValue('schedule.end_days_after_start', '')
    form.setFieldValue('schedule.end_matches_start_holiday', true)
  }

  const handleStartHolidayChange = (startHoliday) => {
    handleStartHolidayScheduleChange({ form, value: startHoliday })
  }

  const handleFixedEndDateChange = (endsOn) => {
    handleFixedEndDateScheduleChange({ form, value: endsOn })
  }

  const clearFixedEndDate = () => {
    clearScheduleSectionError()
    clearScheduleFixedEndDate({ form })
  }

  const persistedAdjustment =
    latestPersistedAdjustment?.id === initialData?.id ? latestPersistedAdjustment : initialData
  const initialDataIsSuppressed = priceAdjustmentIsSuppressed(persistedAdjustment)
  const initialDataCanEnable = Boolean(
    persistedAdjustment && (persistedAdjustment?.status === 'disabled' || initialDataIsSuppressed)
  )
  const scopeAndChangeSectionId = `${id}-scope-and-change-section`
  const scheduleSectionId = `${id}-schedule-section`

  const scrollToSection = (sectionId) => {
    document.getElementById(sectionId)?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const scrollToScheduleSectionOnNextFrame = () => {
    if (typeof window === 'undefined') {
      scrollToSection(scheduleSectionId)
      return
    }

    window.requestAnimationFrame(() => {
      window.setTimeout(() => {
        scrollToSection(scheduleSectionId)
      }, 0)
    })
  }

  const handleSave = () => {
    if (form.getFieldValue('when_type') === PRICE_ADJUSTMENT_WHEN_TYPE_PERMANENT) {
      setPermanentConfirmationOpen(true)
      return
    }

    void form.handleSubmit()
  }

  const resetFormWithServerAdjustment = (adjustment) => {
    if (!adjustment) {
      return
    }

    setLatestPersistedAdjustment(adjustment)
    setTimeout(() => form.reset(mapPriceAdjustmentToFormValues(adjustment)))
  }

  const handleEnable = async () => {
    if (!initialData?.id) {
      return
    }

    try {
      const response = await enableMutation.mutateAsync(initialData.id)
      toast.success('Price change enabled')
      resetFormWithServerAdjustment(response)
    } catch (error) {
      toast.error('Enable failed', {
        description: getPriceAdjustmentErrorMessage(error),
      })
    }
  }

  const handleDisable = async (mode) => {
    if (!initialData?.id) {
      return
    }

    try {
      const response = await disableMutation.mutateAsync({
        priceAdjustmentId: initialData.id,
        mode,
      })
      setDisableDialogOpen(false)
      toast.success(
        mode === PRICE_ADJUSTMENT_DISABLE_MODE_THIS_OCCURRENCE
          ? 'This occurrence skipped'
          : 'Price change disabled'
      )
      resetFormWithServerAdjustment(response)
    } catch (error) {
      toast.error('Disable failed', {
        description: getPriceAdjustmentErrorMessage(error),
      })
    }
  }

  const handleDelete = async () => {
    if (!initialData?.id) {
      return
    }

    try {
      await deleteMutation.mutateAsync(initialData.id)
      toast.success('Price change deleted successfully')
      await navigate({ ...path.priceChanges.index().link, ignoreBlocker: true, replace: true })
      void Promise.resolve()
        .then(() => deleteMutation.cleanupDeletedPriceAdjustment?.(initialData.id))
        .catch(() => undefined)
    } catch (error) {
      toast.error('Failed to delete price change', {
        description: getPriceAdjustmentErrorMessage(error),
      })
    }
  }

  const getRadioButtonGroupProps = (classNames = {}) =>
    showPageTreatment
      ? {
          appearance: 'segmented',
          joined: true,
          selectedVariant: 'selection',
          unselectedVariant: 'outline',
          classNames,
        }
      : {
          classNames,
        }

  return {
    id,
    form,
    initialData,
    persistedAdjustment,
    mutation,
    disableMutation,
    deleteMutation,
    enableMutation,
    path,
    showPageTreatment,
    selectableCollections,
    scheduleMinDate,
    scheduleMaxDate,
    scopeAndChangeSectionId,
    scheduleSectionId,
    disableDialogOpen,
    setDisableDialogOpen,
    permanentConfirmationOpen,
    setPermanentConfirmationOpen,
    initialDataCanEnable,
    clearScopeSectionError,
    clearScheduleSectionError,
    clearWhenSectionError,
    handleScheduleFieldChange,
    rememberCurrentScheduledTimingDraft,
    switchToFixedDateSchedule,
    switchToHolidaySchedule,
    switchToActiveUntilDeletedMode,
    switchToScheduledMode,
    switchToPermanentSchedule,
    handleStartHolidayChange,
    handleFixedEndDateChange,
    clearFixedEndDate,
    scrollToSection,
    scrollToScheduleSectionOnNextFrame,
    handleSave,
    handleEnable,
    handleDisable,
    handleDelete,
    getRadioButtonGroupProps,
  }
}
